import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { Set as CustomSet } from '~commonInfra/utils/Set';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '~src/codegen/Localize';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { reloadSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';
import { selectContentFromAppState } from '~src/spacesPlatform/selectors/ContentSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';

import {
  ADMIN_PROFILE_REMOVE_FROM_SITE,
  AdminProfileRemoveFromSiteActionPayload,
} from '../entityActions/AdminProfileRemoveFromSiteAction';
import {
  ADMIN_PROFILE_REMOVE_FROM_USER,
  AdminProfileRemoveFromUserActionPayload,
} from '../entityActions/AdminProfileRemoveFromUserAction';
import {
  ADMIN_PROFILE_SITE_MEMBERSHIP,
  AdminProfileSiteMembershipActionPayload,
} from '../entityActions/AdminProfileSiteMembershipAction';
import { showAdminProfileUserSiteMembershipDialog } from '../services/AdminProfileUserSiteMembershipDialogAction';
import { RemoveAdminProfileFromSiteAction } from '../services/RemoveAdminProfileFromSiteAction';
import { showRemoveUsersFromAdminProfileDialog } from '../services/RemoveUsersFromAdminProfileDialogAction';

/** Admin profile sites content includes adminProfileAssignedUserCount. */
function getAssignedUserCount(item: IExplorableItemTypeInfo): number {
  const count = (item as unknown as Record<string, unknown>).adminProfileAssignedUserCount;
  return typeof count === 'number' ? count : 0;
}

export function* handleRemoveFromSite(action: AdminProfileRemoveFromSiteActionPayload): SagaIterator {
  try {
    const spaceInfo = (yield select(selectSpaceInfo)) as SpaceInfo | undefined;
    const contentItems: ReadonlyArray<IExplorableItemTypeInfo> =
      (yield select(selectContentFromAppState)) ?? [];

    const profileId = spaceInfo?.item?.id ?? '';
    const profileName = (spaceInfo?.item as { name?: string } | undefined)?.name ?? '';
    const siteIdsToRemoveSet = new Set<string>(action.siteIds);

    // All sites where the profile is currently enabled
    const currentSiteIds = contentItems.map((c) => c.id);

    // Sites the user selected for removal
    const itemsToRemove = contentItems.filter((c) => siteIdsToRemoveSet.has(c.id));
    const sitesToRemove = itemsToRemove.map((c) => ({
      id: c.id,
      name: (c as { name?: string }).name ?? c.id,
    }));

    // Total assigned users across sites being removed (drives the warning message)
    const assignedUsersCount = itemsToRemove
      .map(getAssignedUserCount)
      .reduce((sum, count) => sum + count, 0);

    yield call(
      RemoveAdminProfileFromSiteAction.showRemoveAdminProfileFromSiteDialog,
      profileId,
      profileName,
      sitesToRemove,
      currentSiteIds,
      assignedUsersCount
    );
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleRemoveFromUser(action: AdminProfileRemoveFromUserActionPayload): SagaIterator {
  try {
    // Build the user set from the action payload
    const userSet = new CustomSet<IUserInfo>((userInfo) => userInfo.id);
    action.users.forEach(u =>
      userSet.add({ id: u.id, name: u.name, displayName: u.name, username: u.username } as unknown as IUserInfo)
    );

    // Pass all user LUIDs (one per user-site, not deduped) to removeUsersFromProfile.
    const userLuids = action.users.reduce((acc: string[], u) => acc.concat(u.siteInfos.map(s => s.luid)), []);
    yield call(showRemoveUsersFromAdminProfileDialog, action.profileName, action.profileId, userLuids, userSet);

    // After the user confirms and the API call succeeds, reload the space to reflect
    // the updated user list.
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleAdminProfileSiteMembership(action: AdminProfileSiteMembershipActionPayload): SagaIterator {
  try {
    yield call(showAdminProfileUserSiteMembershipDialog, action.users, action.profileId);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootAdminProfileMenuActionSaga(): SagaIterator {
  yield takeEvery(ADMIN_PROFILE_REMOVE_FROM_SITE, handleRemoveFromSite);
  yield takeEvery(ADMIN_PROFILE_REMOVE_FROM_USER, handleRemoveFromUser);
  yield takeEvery(ADMIN_PROFILE_SITE_MEMBERSHIP, handleAdminProfileSiteMembership);
}
